// features/public-map/presentation/hooks/useMarkerHoverSync.ts
import { useEffect, useRef, MutableRefObject } from 'react';
import mapboxgl from 'mapbox-gl';
import { useHoverStore } from '../../application/hoverStore';

/**
 * Hook to sync hover state between list views and individual map markers.
 * When a property is hovered in the list, this hook highlights the corresponding
 * marker on the map by adding a CSS class to the marker's DOM element.
 *
 * Note: Uses direct DOM manipulation to avoid re-rendering markers.
 * If performance issues arise with large numbers of properties,
 * consider using Zustand selectors more aggressively to minimize re-renders.
 */
export function useMarkerHoverSync(
  markersRef: MutableRefObject<Record<string, mapboxgl.Marker>>
) {
  const hoveredPropertyId = useHoverStore(s => s.hoveredPropertyId);
  const prevHoveredRef = useRef<string | null>(null);

  useEffect(() => {
    // Remove previous highlight
    if (prevHoveredRef.current && markersRef.current[prevHoveredRef.current]) {
      const prevMarker = markersRef.current[prevHoveredRef.current];
      const element = prevMarker.getElement();
      if (element) {
        element.classList.remove('marker-hovered');
      }
    }

    // Add new highlight
    if (hoveredPropertyId && markersRef.current[hoveredPropertyId]) {
      const marker = markersRef.current[hoveredPropertyId];
      const element = marker.getElement();
      if (element) {
        element.classList.add('marker-hovered');
      }
    }

    prevHoveredRef.current = hoveredPropertyId;
  }, [hoveredPropertyId, markersRef]);
}
